import { useEffect, useState } from "react";
import { Sparkles, Loader2, CheckCircle, XCircle, Download, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * AriaDeliveryProgress — surfaces an ARIA agent's auto-delivery in the
 * job detail modal. Polls `/marketplace/jobs/{id}/aria-delivery` every
 * 3 seconds while the delivery is running; falls back to Socket.IO
 * events when available.
 *
 * Renders:
 *   • Pending-dataset — gold "Upload dataset to start" card (analyst only)
 *   • Running   — gold "ARIA is working" card with progress bar + step
 *   • Delivered — green "Delivery ready" card with download buttons
 *   • Failed    — red "Delivery failed" card with retry hint
 *   • None      — nothing (returns null)
 *
 * Props:
 *   • jobId: the gig id
 *   • visible: only mount/poll when the parent shows this section
 *   • onPendingDataset: callback fired the first time we see
 *     status='pending_dataset' — lets the parent surface a "Upload CSV"
 *     CTA in the right place.
 */
export default function AriaDeliveryProgress({ jobId, visible = true, onPendingDataset }) {
  const [delivery, setDelivery] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!visible || !jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get(`/marketplace/jobs/${jobId}/aria-delivery`);
        if (cancelled) return;
        const d = res.data;
        setDelivery(d.status === "none" ? null : d);
        if (d.status === "pending_dataset") onPendingDataset?.(d);
      } catch { /* ignore */ }
    };
    tick();
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [jobId, visible, onPendingDataset]);

  const downloadArtifact = async (idx, name) => {
    if (!delivery?.id) return;
    setDownloading(idx);
    try {
      const res = await api.get(
        `/marketplace/aria-delivery/${delivery.id}/artifact/${idx}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || "artifact";
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Couldn't download — sign in as the job poster");
    } finally {
      setDownloading(null);
    }
  };

  if (!delivery) return null;

  const pct = delivery.progress_pct ?? (
    delivery.status === "delivered" ? 100 :
    delivery.status === "failed" ? 100 :
    delivery.status === "running" ? 45 : 0
  );
  const isRunning = delivery.status === "running";
  const isDelivered = delivery.status === "delivered";
  const isFailed = delivery.status === "failed";
  const isPendingDataset = delivery.status === "pending_dataset";

  const meta = delivery.agent_id === "aria-engineer"
    ? { emoji: "👨‍💻", label: "ARIA-Engineer" }
    : delivery.agent_id === "aria-writer"
      ? { emoji: "✍️", label: "ARIA-Writer" }
      : delivery.agent_id === "aria-designer"
        ? { emoji: "🎨", label: "ARIA-Designer" }
        : delivery.agent_id === "aria-analyst"
          ? { emoji: "📊", label: "ARIA-Analyst" }
          : { emoji: "✨", label: "ARIA Agent" };

  return (
    <div data-testid="aria-delivery-card"
      className={`p-4 border rounded-md ${
        isDelivered
          ? "border-emerald-400/40 bg-emerald-500/[0.06]"
          : isFailed
            ? "border-rose-400/40 bg-rose-500/[0.06]"
            : isPendingDataset
              ? "border-amber-400/40 bg-amber-500/[0.06]"
              : "border-gold/40 bg-gradient-to-br from-gold/[0.08] to-transparent"
      }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{meta.emoji}</span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-1.5">
              <Sparkles size={10} /> {meta.label} delivery
            </p>
            <p className="text-xs text-white font-body">
              {isRunning && `Working — ${pct}%`}
              {isDelivered && "Delivered · review & approve to release escrow"}
              {isFailed && "Delivery failed — request a rerun or pick a human"}
              {isPendingDataset && "Waiting on your dataset — upload a CSV / XLSX to start"}
            </p>
          </div>
        </div>
        {isRunning && <Loader2 size={16} className="animate-spin text-gold" />}
        {isDelivered && <CheckCircle size={16} className="text-emerald-400" />}
        {isFailed && <XCircle size={16} className="text-rose-400" />}
        {isPendingDataset && <FileSpreadsheet size={16} className="text-amber-300" />}
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div data-testid="aria-progress-bar"
          className="h-1.5 bg-black/40 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      {/* Artifacts list (delivered) */}
      {isDelivered && (delivery.artifacts || []).length > 0 && (
        <div data-testid="aria-artifacts" className="space-y-1.5">
          {(delivery.artifacts || []).map((a, i) => (
            <button key={i}
              data-testid={`aria-artifact-${i}`}
              onClick={() => downloadArtifact(i, a.name)}
              disabled={downloading === i}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-black/40 border border-white/10 hover:border-gold/40 rounded transition-colors text-left disabled:opacity-50">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={12} className="text-gold flex-shrink-0" />
                <p className="text-[11px] text-white truncate">{a.name}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] text-white/45">{Math.round((a.size_bytes || 0) / 1024)}KB</span>
                {downloading === i
                  ? <Loader2 size={11} className="animate-spin text-gold/70" />
                  : <Download size={11} className="text-gold/70" />}
              </div>
            </button>
          ))}
          {delivery.summary && (
            <p className="text-[11px] text-white/55 italic pt-1.5">{delivery.summary}</p>
          )}
        </div>
      )}

      {/* Error message (failed) */}
      {isFailed && delivery.error && (
        <p data-testid="aria-error" className="text-[11px] text-rose-300/80 mt-1">
          {delivery.error}
        </p>
      )}
    </div>
  );
}
